import { useState, useRef, useCallback, memo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Constants for better maintainability
const HOVER_TRANSITION = {
  clipPath: {
    initial: "circle(0% at 50% 50%)",
    expanded: (x, y) => `circle(150% at ${x}% ${y}%)`
  },
  timing: "0.5s cubic-bezier(0.4, 0, 0.2, 1)"
};

const THEME_DEFAULTS = {
  active: "bg-[#1C1F2A]",
  hover: "bg-white/5",
  text: "text-grey",
  activeText: "text-dark",
  border: "border-primary"
};

const ThemeNavLink = memo(({
  to,
  label,
  icon,
  isOpen = true,
  className = "",
  activeColor = THEME_DEFAULTS.active,
  hoverColor = THEME_DEFAULTS.hover,
  textColor = THEME_DEFAULTS.text,
  activeTextColor = THEME_DEFAULTS.activeText,
  borderColor = THEME_DEFAULTS.border
}) => {
  const location = useLocation();
  const [hoverState, setHoverState] = useState({ 
    isHovered: false, 
    position: { x: 0, y: 0 } 
  });
  const linkRef = useRef(null);

  // Check if current route is active
  const isCurrentRoute = location.pathname === to;

  // Memoized mouse handler
  const handleMouseEnter = useCallback((e) => {
    if (!linkRef.current) return;

    const rect = linkRef.current.getBoundingClientRect();
    const position = {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100
    };

    setHoverState({ isHovered: true, position });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoverState(prev => ({ ...prev, isHovered: false }));
  }, []);

  // Memoized class generator with isCurrentRoute
  const getAnimatedClasses = useCallback(({ isActive, isHovered }) => ({
    link: `
      relative group flex items-center gap-4 px-3 py-2 rounded-lg 
      transition-all duration-300 text-sm font-medium overflow-hidden
      ${isActive ? `${activeColor} ${activeTextColor} border-l-4 ${borderColor}` : textColor}
      ${className}
    `,
    icon: `
      text-base w-5 transition-colors duration-300
      ${isActive ? activeTextColor : textColor}
      ${isHovered ? activeTextColor : ''}
    `,
    text: `
      relative transition-all duration-300
      ${isActive ? activeTextColor : textColor}
      ${isHovered ? activeTextColor : ''}
    `
  }), [activeColor, activeTextColor, borderColor, textColor, className]);

  return (
    <NavLink
      ref={linkRef}
      to={to}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={({ isActive }) => getAnimatedClasses({ 
        isActive, 
        isHovered: hoverState.isHovered 
      }).link}
    >
      {/* Hover Effect */}
      <div className="absolute inset-0 overflow-hidden"
        style={{
          clipPath: hoverState.isHovered 
            ? HOVER_TRANSITION.clipPath.expanded(hoverState.position.x, hoverState.position.y)
            : HOVER_TRANSITION.clipPath.initial,
          transition: `clip-path ${HOVER_TRANSITION.timing}`
        }}>
        <div className={`absolute inset-0 ${hoverColor}`} />
      </div>

      {/* Icon */}
      <div className={`relative transition-transform duration-300 ${
        hoverState.isHovered ? 'scale-110' : ''
      }`}>
        <FontAwesomeIcon 
          icon={icon} 
          className={getAnimatedClasses({ 
            isActive: isCurrentRoute, 
            isHovered: hoverState.isHovered 
          }).icon}
        />
      </div>

      {/* Label */}
      {isOpen && (
        <span 
          className={getAnimatedClasses({ 
            isActive: isCurrentRoute, 
            isHovered: hoverState.isHovered 
          }).text}
          style={{
            transform: `translateX(${hoverState.isHovered ? '3px' : '0'})`
          }}
        >
          {label}
        </span>
      )}

      {/* Active Indicator */}
      {isCurrentRoute && (
        <div 
          className={`absolute left-0 top-0 bottom-0 w-1 ${borderColor}
            transition-all duration-300 transform origin-left
            ${hoverState.isHovered ? 'scale-y-100' : 'scale-y-90'}
          `} 
        />
      )}
    </NavLink>
  );
});

ThemeNavLink.propTypes = {
  to: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  isOpen: PropTypes.bool,
  className: PropTypes.string,
  activeColor: PropTypes.string,
  hoverColor: PropTypes.string,
  textColor: PropTypes.string,
  activeTextColor: PropTypes.string,
  borderColor: PropTypes.string
};

ThemeNavLink.displayName = 'ThemeNavLink';

export default ThemeNavLink;